import React, { useState, useEffect } from 'react';

// Skeleton loading component for the TripPlanner interface with sequential loading
const SkeletonLoader = () => {
  const [loadingStage, setLoadingStage] = useState(0);
  const [showChatContent, setShowChatContent] = useState(false);
  const [showItineraryContent, setShowItineraryContent] = useState(false);
  const [itineraryItems, setItineraryItems] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState([
    'Initializing AI travel assistant...',
    'Analyzing your hotel reservation details...',
    'Understanding your travel preferences...',
    'Identifying attractions near The Plaza Hotel...',
    'Generating personalized recommendations...',
    'Creating your customized itinerary...'
  ]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  
  useEffect(() => {
    // Create a series of timers for the loading messages (4 seconds each)
    const messageInterval = 4000;
    
    // Change messages at regular intervals
    const messageTimer = setInterval(() => {
      setCurrentMessageIndex(prev => {
        // If we've reached the end, stay on the last message
        if (prev >= loadingMessages.length - 1) {
          clearInterval(messageTimer);
          return prev;
        }
        return prev + 1;
      });
    }, messageInterval);
    
    // Stage 0: Initial blank layout (already shown at render)
    
    // Stage 1: Show chat panel (40% width)
    const stage1Timer = setTimeout(() => {
      setLoadingStage(1);
    }, 2000);
    
    // Stage 2: Chat panel content appears
    const stage2Timer = setTimeout(() => {
      setShowChatContent(true);
    }, 6000);
    
    // Stage 3: Show itinerary content (60% width)
    const stage3Timer = setTimeout(() => {
      setLoadingStage(2);
      setShowItineraryContent(true);
      
      // Add itinerary items one by one with longer delays
      const totalItems = 5;
      for (let i = 0; i < totalItems; i++) {
        setTimeout(() => {
          setItineraryItems(prev => [...prev, i]);
        }, i * 1200);
      }
    }, 12000);
    
    return () => {
      clearInterval(messageTimer);
      clearTimeout(stage1Timer);
      clearTimeout(stage2Timer);
      clearTimeout(stage3Timer);
    };
  }, [loadingMessages.length]);
  
  // Calculate current content widths based on loading stage
  const getChatWidth = () => {
    switch(loadingStage) {
      case 0: return 'w-0 opacity-0'; // Hidden initially
      case 1: 
      case 2: return 'w-2/5 opacity-100'; // 40% width when visible
      default: return 'w-2/5 opacity-100';
    }
  };
  
  const getItineraryWidth = () => {
    switch(loadingStage) {
      case 0:
      case 1: return 'w-0 opacity-0'; // Hidden initially
      case 2: return 'w-3/5 opacity-100'; // 60% width when visible
      default: return 'w-0 opacity-0';
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100 relative">
      {/* Header Skeleton */}
      <div className="bg-white border-b border-gray-200 shadow-xs">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="animate-pulse h-8 w-32 bg-gray-200 rounded-sm"></div>
            <div className="flex space-x-4">
              <div className="animate-pulse h-8 w-8 bg-gray-200 rounded-full"></div>
              <div className="animate-pulse h-8 w-8 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className="flex grow overflow-hidden">
        {/* Main Content Skeleton - Two-column layout */}
        <div className="grow overflow-hidden flex">
          {/* Chat Panel Skeleton (40% width) */}
          <div 
            className={`transition-all duration-1000 ease-in-out ${getChatWidth()} flex flex-col bg-white border-r border-gray-200 overflow-hidden`}
          >
            {/* Header */}
            <div className="border-b p-4">
              <div className="animate-pulse h-6 w-40 bg-gray-200 rounded-sm"></div>
            </div>
            
            {/* Chat Content */}
            <div className="grow p-4 overflow-auto">
              {showChatContent && (
                <>
                  {/* First assistant message */}
                  <div className="flex mb-6" style={{
                    animation: 'slideInFromLeft 0.8s ease-out forwards',
                    opacity: 0
                  }}>
                    <div className="w-8 h-8 rounded-full bg-blue-200 mr-3 shrink-0"></div>
                    <div>
                      <div className="bg-blue-50 rounded-lg p-3 mb-1">
                        <div className="h-4 bg-gray-200 rounded-sm w-full mb-2 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded-sm w-5/6 mb-2 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded-sm w-4/6 animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* User message */}
                  <div 
                    className="flex mb-6 justify-end"
                    style={{
                      animation: 'slideInFromRight 0.8s ease-out 0.6s forwards',
                      opacity: 0
                    }}
                  >
                    <div>
                      <div className="bg-blue-100 rounded-lg p-3 mb-1">
                        <div className="h-4 bg-gray-200 rounded-sm w-full mb-2 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded-sm w-5/6 animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Typing indicator */}
                  <div 
                    className="flex mb-6"
                    style={{
                      animation: 'slideInFromLeft 0.8s ease-out 1.2s forwards',
                      opacity: 0
                    }}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-200 mr-3 shrink-0"></div>
                    <div className="bg-blue-50 rounded-lg p-3 flex items-center">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Input area skeleton */}
            <div className="border-t p-4">
              <div className="bg-gray-100 rounded-lg p-2 flex items-center">
                <div className="animate-pulse h-8 w-full bg-gray-200 rounded-md"></div>
                <div className="animate-pulse h-8 w-8 bg-gray-200 rounded-full ml-2"></div>
              </div>
            </div>
          </div>
          
          {/* Itinerary Panel Skeleton (60% width) */}
          <div 
            className={`transition-all duration-1000 ease-in-out ${getItineraryWidth()} flex-col bg-white overflow-hidden`}
          >
            <div className="border-b p-4">
              <div className="animate-pulse h-6 w-40 bg-gray-200 rounded-sm"></div>
            </div>
            
            <div className="grow p-4 overflow-auto">
              {/* Timeline items skeleton */}
              <div className="relative">
                {/* Timeline line */}
                {showItineraryContent && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-px bg-gray-200"
                    style={{
                      animation: 'expandDown 1.5s ease-out forwards'
                    }}
                  ></div>
                )}
                
                {/* Timeline items */}
                {itineraryItems.map((i) => (
                  <div 
                    key={i} 
                    className="mb-8"
                    style={{
                      animation: 'slideInFromRight 0.8s ease-out forwards',
                      opacity: 0
                    }}
                  >
                    <div className="flex items-start">
                      <div className="w-4 h-4 rounded-full bg-gray-300 z-10 mr-4"></div>
                      <div className="grow">
                        <div className="h-5 w-20 bg-gray-200 rounded-sm mb-2 animate-pulse"></div>
                        <div className="bg-gray-100 rounded-lg p-4">
                          <div className="h-5 w-32 bg-gray-200 rounded-sm mb-3 animate-pulse"></div>
                          <div className="h-4 w-full bg-gray-200 rounded-sm mb-2 animate-pulse"></div>
                          <div className="h-4 w-5/6 bg-gray-200 rounded-sm mb-2 animate-pulse"></div>
                          <div className="h-4 w-4/6 bg-gray-200 rounded-sm animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Centered Large Loading Message Overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div className="bg-blue-600 text-white px-8 py-6 rounded-xl shadow-2xl flex flex-col items-center max-w-xl">
          <svg className="animate-spin h-10 w-10 text-white mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <div className="text-2xl font-semibold text-center">
            {loadingMessages[currentMessageIndex]}
          </div>
        </div>
      </div>
      
      {/* Custom animations */}
      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInFromLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInFromRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes expandDown {
          from { height: 0; }
          to { height: 100%; }
        }
      `}</style>
    </div>
  );
};

export default SkeletonLoader;